import React from 'react';
import { Upload, FileVideo, Video, Loader } from 'lucide-react';
import { analyzeVideo } from '../utils/api';

const UploadSection = ({
    file,
    setFile,
    processing,
    setProcessing,
    setResult,
    setError,
    setVideoPreview,
    videoPreview,
    resetApp
}) => {

    const handleFileUpload = (e) => {
        const uploadedFile = e.target.files[0];
        if (uploadedFile) {
            if (!uploadedFile.type.startsWith('video/')) {
                setError('Please upload a valid video file');
                return;
            }

            setFile(uploadedFile);
            setError(null);
            setResult(null);

            const url = URL.createObjectURL(uploadedFile);
            setVideoPreview(url);
        }
    };

    const handleAnalyze = async () => {
        if (!file) {
            setError('Please upload a video first');
            return;
        }

        setProcessing(true);
        setError(null);

        try {
            const result = await analyzeVideo(file); // Use the utility function
            setResult(result);
        } catch (err) {
            setError(err.message || 'Error processing video. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Upload Video</h2>

            {!file ? (
                <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-indigo-300 rounded-lg cursor-pointer bg-indigo-50 hover:bg-indigo-100 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        <Upload className="text-indigo-600 mb-4" size={48} />
                        <p className="mb-2 text-lg font-semibold text-gray-700">Click to upload video</p>
                        <p className="text-sm text-gray-500">MP4, AVI, MOV supported (MAX. 100MB)</p>
                    </div>
                    <input
                        type="file"
                        className="hidden"
                        accept="video/*"
                        onChange={handleFileUpload}
                    />
                </label>
            ) : (
                <div className="space-y-4">
                    <div className="flex items-center justify-between bg-indigo-50 p-4 rounded-lg">
                        <div className="flex items-center gap-3">
                            <FileVideo className="text-indigo-600" size={32} />
                            <div>
                                <p className="font-semibold text-gray-800">{file.name}</p>
                                <p className="text-sm text-gray-600">
                                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={resetApp}
                            className="text-red-600 hover:text-red-800 font-medium"
                        >
                            Remove
                        </button>
                    </div>

                    {videoPreview && (
                        <div className="bg-gray-50 rounded-lg p-4">
                            <video
                                src={videoPreview}
                                controls
                                className="w-full max-h-64 rounded-lg"
                            />
                        </div>
                    )}

                    <button
                        onClick={handleAnalyze}
                        disabled={processing}
                        className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    >
                        {processing ? (
                            <>
                                <Loader className="animate-spin" size={20} />
                                Processing Video...
                            </>
                        ) : (
                            <>
                                <Video size={20} />
                                Analyze Video
                            </>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
};

export default UploadSection;